import dayjs, { Dayjs } from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import { ChangeEvent, ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { RegisterBanksSelect } from '@/modules/common/components/RegisterBanksSelect.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { useCurrentUserRegistersQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { useCreateBuyBackMutation } from '@/modules/issuer-dashboard/queries/buyBack.tsx';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import {
  Alert,
  Button,
  CalendarInput,
  CalendarInputProps, // CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginateOption,
  TextInput,
} from 'ui';
import { InfoCircleBlueIcon } from 'ui/icons';
import { BankTypeEnum, CreateBuyBackEventRequest, RegisterBankExtended, RegisterType } from 'api';

export interface IssuerAddBuyBackModalProps {
  predefinedClassId?: number;
  open: boolean;
  onClose: () => void;
  defaultAllowsFractionalShares: boolean;
}

const validationSchema = yup.object({
  register_class_id: yup
    .number()
    .moreThan(0, 'Select security class')
    .required('Select security class'),
  price_per_share: yup
    .number()
    .moreThan(0.099, 'Price per share cannot be lower than $0.1')
    // .when(['total_buy_back_offer'], ([total_buy_back_offer], schema) => {
    //   return schema.max(
    //     total_buy_back_offer,
    //     'Price per share cannot be higher than total buy back offer',
    //   );
    // })
    .required('Enter price per share'),
  total_buy_back_offer: yup
    .number()
    .moreThan(0.099, 'Total pay back cannot be lower than $0.1')
    .required('Enter total buy offer'),
  open_event_date: yup.string().trim().not(['']).required('Select event date'),
  close_event_date: yup.string().trim().not(['']).required('Select event date'),
  memo: yup.string().trim(),
  bank_id: yup.number().moreThan(0, 'This field is required').required('This field is required'),
});

const validationSchemaSelective = yup.object({
  register_class_id: yup
    .number()
    .moreThan(1, 'Select security class')
    .required('Select security class'),
  price_per_share: yup
    .number()
    .moreThan(0.099, 'Price per share cannot be lower than $0.1')
    // .when(['total_buy_back_offer'], ([total_buy_back_offer], schema) => {
    //   return schema.max(
    //     total_buy_back_offer,
    //     'Price per share cannot be higher than total buy back offer',
    //   );
    // })
    .required('Enter price per share'),
  total_buy_back_offer: yup
    .number()
    .moreThan(0.099, 'Total pay back cannot be lower than $0.1')
    .required('Enter total buy offer'),
  open_event_date: yup.string().trim().not(['']).required('Select event date'),
  close_event_date: yup.string().trim().not(['']).required('Select event date'),
  memo: yup.string().trim(),
  shareholder_ids: yup
    .array()
    .required('Select at least one shareholder')
    .min(1, 'Select at least one shareholder'),
  bank_id: yup.number().moreThan(0, 'This field is required').required('This field is required'),
});

const initialValues: CreateBuyBackEventRequest = {
  price_per_share: 0.1,
  register_class_id: 0,
  total_buy_back_offer: 0.1,
  open_event_date: '',
  close_event_date: '',
  memo: '',
  shareholder_ids: [],
  bank_id: 0,
};

enum BuyBackType {
  EqualAccess,
  Selective,
}

const typeOptions = [
  { label: 'Equal access', value: BuyBackType.EqualAccess },
  { label: 'Selective', value: BuyBackType.Selective },
];

export const IssuerAddBuyBackModal = ({
  open,
  predefinedClassId,
  onClose,
  defaultAllowsFractionalShares,
}: IssuerAddBuyBackModalProps) => {
  const [type, setType] = useState<BuyBackType | null>(BuyBackType.EqualAccess);
  const [params, setParams] = useSearchParams();
  const register_id = params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '';

  const allowsFractionalShares =
    useGetRegister(Number(register_id))?.allows_fractional_shares ?? defaultAllowsFractionalShares;

  const formik = useFormik<CreateBuyBackEventRequest>({
    initialValues: predefinedClassId
      ? {
          ...{
            ...initialValues,
            price_per_share: allowsFractionalShares ? 0.1 : 1,
            total_buy_back_offer: allowsFractionalShares ? 0.1 : 1,
          },
          register_class_id: predefinedClassId,
        }
      : {
          ...initialValues,
          price_per_share: allowsFractionalShares ? 0.1 : 1,
          total_buy_back_offer: allowsFractionalShares ? 0.1 : 1,
        },
    validationSchema:
      type === BuyBackType.Selective
        ? validationSchemaSelective //with shareholder_ids
        : validationSchema,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async values => {
      if (+values.price_per_share > values.total_buy_back_offer) {
        formik.setFieldError(
          'price_per_share',
          'Price per share cannot be higher than total buy back offer ' +
            values.total_buy_back_offer,
        );
        return;
      }

      const isCurrentDay = dayjs(values.open_event_date)?.isSame(dayjs(), 'd');

      const correctedOpenDate = isCurrentDay
        ? dayjs().add(2, 'm').toISOString()
        : values.open_event_date;

      await createBuyBack({ ...values, open_event_date: correctedOpenDate });
    },
  });

  const { data: registers, isLoading: isRegistersLoading } = useCurrentUserRegistersQuery(true);

  const { data: registerClasses, isLoading: isRegisterClassesLoading } =
    useIssuerRegisterClassListQuery({ search: undefined }, Number(register_id), false);

  const { data: shareholders, isLoading: isShareholdersLoading } = useRegisterShareholderList(
    { register_id: +register_id, register_class_id: formik.values.register_class_id },
    { disabled: !register_id || type === BuyBackType.EqualAccess },
  );

  const { mutateAsync: createBuyBack, isLoading: isBuyBackCreation } = useCreateBuyBackMutation({
    setErrors: formik.setErrors,
    onSuccess: () => {
      formik.resetForm();
      onClose();
    },
  });

  const isLoading =
    isRegistersLoading ||
    isRegisterClassesLoading ||
    isBuyBackCreation ||
    (isShareholdersLoading && type === BuyBackType.Selective);

  const registersOptions = useMemo<SelectOption<number>[]>(
    () =>
      (registers?.data || []).reduce<SelectOption<number>[]>(
        (acc, { register_id, register_obj }) =>
          register_obj && register_obj.type === RegisterType.Company
            ? [
                ...acc,
                {
                  value: register_id,
                  label: register_obj.name,
                },
              ]
            : acc,
        [],
      ),
    [registers],
  );

  const registerClassesOptions = useMemo<SelectOption<number>[]>(
    () =>
      (registerClasses?.registers || []).map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      })),
    [registerClasses?.registers],
  );

  const shareholdersOptions = useMemo(
    () =>
      (shareholders?.shareholders || []).map(({ id, user }) => ({
        value: id,
        label: `${user?.first_name} ${user?.last_name}`,
      })),
    [shareholders?.shareholders],
  );

  const handleChangeType = useCallback(
    (value: BuyBackType | null) => {
      setType(value);
      formik.setFieldValue('shareholder_ids', []);
      formik.setFieldError('shareholder_ids', '');
    },
    [formik],
  );

  const handleChangeShareholders = (value: number[] | null) => {
    formik.setFieldValue('shareholder_ids', value || []);
    formik.setFieldError('shareholder_ids', '');
  };

  const handleChangeRegister = useCallback(
    (registerId: number | null) => {
      formik.setFieldValue('shareholder_ids', []);

      if (registerId) {
        params.set(REGISTER_ID_SEARCH_PARAM_KEY, registerId?.toString());
      }
      setParams(params, { replace: false });
    },
    [formik, params, setParams],
  );

  const handleChangeRegisterClass = useCallback(
    (value: number | null) => {
      formik.setFieldValue('register_class_id', value);
      formik.setFieldValue('shareholder_ids', []);
      formik.setFieldError('shareholder_ids', '');
    },
    [formik],
  );

  const handleChangePricePerShare = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      formik.setFieldError('price_per_share', '');
      if (value === '') {
        formik.setFieldValue('price_per_share', '');
        return;
      }

      const valueRounded = Number(value);
      // const valueRounded = allowsFractionalShares
      //   ? Number(value)
      //   : Number(value?.replace(/[.,]/g, '') || 1);

      if (valueRounded > formik.values.total_buy_back_offer) {
        formik.setFieldError(
          'price_per_share',
          'Price per share cannot be higher than total buy back offer ' +
            formik.values.total_buy_back_offer,
        );
      }
      formik.setFieldValue('price_per_share', valueRounded);
    },
    [formik],
  );

  const handleChangeTotalBuyBackOffer: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => {
      const value = event.target.value;
      formik.setFieldError('total_buy_back_offer', '');
      formik.setFieldError('price_per_share', '');
      if (value === '') {
        formik.setFieldValue('total_buy_back_offer', '');
        return;
      }

      const valueRounded = allowsFractionalShares
        ? Number(value)
        : Number(value?.replace(/[.,]/g, '') || 1);

      formik.setFieldValue('total_buy_back_offer', valueRounded);
    },
    [allowsFractionalShares, formik],
  );

  const handleChangeDate: CalendarInputProps['rangeOnChange'] = dates => {
    if (!dates || !dates.length) {
      formik.setFieldValue('open_event_date', '');
      formik.setFieldValue('close_event_date', '');
      return;
    }

    const isCurrentDay = dates[0]?.isSame(dayjs(), 'd');

    const correctedOpenDate = isCurrentDay
      ? dayjs().add(2, 'm').toISOString()
      : dates[0]?.toISOString();

    formik.setFieldValue('open_event_date', correctedOpenDate);
    formik.setFieldValue('close_event_date', dates[1]?.endOf('d').toISOString() || '');
  };

  const handleCheckDisabledDate = useCallback((date: Dayjs) => {
    return date.isBefore(dayjs().startOf('day'));
  }, []);

  useEffect(() => {
    const isPresentFirstRegisterClassOption = !!registerClassesOptions[0];
    const isUnappropriatedClassSelected = !registerClassesOptions.find(
      ({ value }) => value === +(formik.values.register_class_id ?? 0),
    );

    if (isPresentFirstRegisterClassOption && isUnappropriatedClassSelected) {
      formik.setFieldValue('register_class_id', registerClassesOptions[0].value);
    }
  }, [formik, register_id, registersOptions, registerClassesOptions]);

  const handleChangeBank = (item: SelectPaginateOption<RegisterBankExtended> | null) => {
    formik.setFieldValue('bank_id', item?.value.id);
    formik.setFieldError('bank_id', '');
  };

  return (
    <ModalContentWrapper open={open} title="Add new buy back" onClose={onClose}>
      <FormikProvider value={formik}>
        <form className="flex flex-col gap-8" onSubmit={preventFormDefault}>
          <Select
            isClearable={true}
            label="Type"
            value={type}
            options={typeOptions}
            onChange={handleChangeType}
          />
          {type === BuyBackType.Selective && (
            <Select
              isMulti={true}
              isClearable={true}
              label="Shareholders"
              value={formik.values.shareholder_ids}
              onChange={handleChangeShareholders}
              options={shareholdersOptions}
              isLoading={isShareholdersLoading}
              error={formik.errors.shareholder_ids}
            />
          )}
          <Select
            label="Register"
            disabled={!!predefinedClassId}
            options={registersOptions}
            value={Number(register_id)}
            onChange={handleChangeRegister}
            isLoading={isRegistersLoading}
          />
          <RegisterBanksSelect
            key={`register_bank_${register_id}`}
            registerId={Number(register_id)}
            onChange={handleChangeBank}
            value={formik.values.bank_id}
            label="Bank"
            clearable={true}
            error={formik.errors.bank_id}
            needBankType={BankTypeEnum.Redemption}
            is_overall={true}
          />
          <Select
            key={`register_class_${register_id}`}
            label="Class"
            disabled={!!predefinedClassId}
            options={registerClassesOptions}
            value={formik.values.register_class_id}
            onChange={handleChangeRegisterClass}
            isLoading={isRegisterClassesLoading}
            error={formik.errors.register_class_id}
          />
          <TextInput
            name="price_per_share"
            label="Price per share"
            type="number"
            min={allowsFractionalShares ? 0.1 : 1}
            step={allowsFractionalShares ? 0.1 : 1}
            // max={formik.values.total_buy_back_offer}
            value={formik.values.price_per_share}
            onChange={handleChangePricePerShare}
            error={formik.errors.price_per_share}
          />
          <TextInput
            name="total_buy_back_offer"
            label="Total buy back offer"
            type="number"
            min={allowsFractionalShares ? 0.1 : 1}
            step={allowsFractionalShares ? 0.1 : 1}
            value={formik.values.total_buy_back_offer}
            onChange={handleChangeTotalBuyBackOffer}
            error={formik.errors.total_buy_back_offer}
          />
          <CalendarInput
            range
            label="Date"
            disabledDate={handleCheckDisabledDate}
            error={formik.errors.open_event_date || formik.errors.close_event_date}
            rangeOnChange={handleChangeDate}
          />
          {/*<CustomCheckbox label="Investor can edit amount" />*/}
          <Alert
            variant="neutral-2"
            icon={InfoCircleBlueIcon}
            label={
              'This will create transactions of "Buy back" type for all applicable investors. ' +
              'Once they approve them, you need to send the bank payments and confirm them in Compli One ' +
              'in order for the transaction to be completed.'
            }
            className="!mb-0"
          />
          <Button
            type="submit"
            loading={isLoading}
            disabled={isLoading}
            onClick={formik.handleSubmit}
          >
            Create buy back
          </Button>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
